import { Avatar, Span, isObject, isString } from "@hopper-ui/components";
import { userDetailsQueryOptions } from "../../api/routes/odata/details.ts";
import { MIN_USER_SEARCH_TERM_LENGTH, searchUsersQueryOptions } from "../../api/routes/odata/search.ts";
import { isEdmGuid } from "@sg-protect/core";
import {
    type ProtectRecentlyViewedEntry,
    type ProtectSearchProviderOptions,
    type ProtectSearchResult,
    useSession
} from "@sg-protect/shell";
import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { generatePath, useMatch } from "react-router";
import { getPaths } from "../../paths.ts";
import { useSearchPaths } from "./searchPaths.ts";

const PROVIDER_ID = "people";

const PERSON_TRIGGER = "Person";

const RECENT_PERSON_TRIGGER = "Recently Viewed Person";

interface RecentPersonData {
    jobTitle: string | null;
    email: string | null;
}

function isRecentPersonData(value: unknown): value is RecentPersonData {
    if (!isObject(value)) {
        return false;
    }

    const hasJobTitle = value.jobTitle == null || isString(value.jobTitle);
    const hasEmail = value.email == null || isString(value.email);

    return hasJobTitle && hasEmail;
}

function isCurrentUser(email: string | null | undefined, sessionEmail: string) {
    if (!email || !sessionEmail) {
        return false;
    }

    return email.toLowerCase() === sessionEmail.toLowerCase();
}

function toDisplayName(displayName: string, isSelf: boolean) {
    return isSelf ? `${displayName} (you)` : displayName;
}

function useResults(query: string, isEnabled: boolean) {
    const paths = useSearchPaths();
    const session = useSession();

    const {
        data: people,
        isFetching,
        isError
    } = useQuery({
        ...searchUsersQueryOptions(query),
        enabled: isEnabled
    });

    const results = useMemo(() => {
        if (people === undefined || paths === undefined) {
            return undefined;
        }

        return people.map<ProtectSearchResult>(user => ({
            id: user.id,
            displayName: toDisplayName(user.displayName, isCurrentUser(user.email, session.email)),
            to: generatePath(paths.userDetails, { userId: user.id }),
            trackingTrigger: PERSON_TRIGGER,
            icon: <Avatar name={user.displayName} />,
            description: user.jobTitle
        }));
    }, [people, paths, session.email]);

    return { results, isFetching, isError };
}

function useRecentResults(entries: ProtectRecentlyViewedEntry[]) {
    const paths = useSearchPaths();
    const session = useSession();

    return useMemo(() => {
        if (paths === undefined) {
            return [];
        }

        return entries.flatMap<ProtectSearchResult>(entry =>
            isRecentPersonData(entry.data)
                ? [
                      {
                          id: entry.id,
                          displayName: toDisplayName(entry.displayName, isCurrentUser(entry.data.email, session.email)),
                          to: generatePath(paths.userDetails, { userId: entry.id }),
                          trackingTrigger: RECENT_PERSON_TRIGGER,
                          icon: <Avatar name={entry.displayName} />,
                          description: entry.data.jobTitle
                      }
                  ]
                : []
        );
    }, [entries, paths, session.email]);
}

function useVisitedEntry() {
    const match = useMatch({ path: getPaths().userDetails, end: false });
    const userId = match?.params.userId;

    const { data: user } = useQuery({
        ...userDetailsQueryOptions(userId ?? ""),
        enabled: isEdmGuid(userId)
    });

    return useMemo(() => {
        if (!user?.id || !user.displayName) {
            return undefined;
        }

        return {
            providerId: PROVIDER_ID,
            id: user.id,
            displayName: user.displayName,
            data: { jobTitle: user.jobTitle ?? null, email: user.email ?? null } satisfies RecentPersonData
        };
    }, [user]);
}

export const peopleSearchProvider: ProtectSearchProviderOptions = {
    id: PROVIDER_ID,
    priority: 10,
    minQueryLength: MIN_USER_SEARCH_TERM_LENGTH,
    entityLabels: ["people"],
    sectionLabel: <Span data-public>People</Span>,
    recentSectionLabel: <Span data-public>Recently viewed people</Span>,
    useResults,
    useRecentResults,
    useVisitedEntry
};
